import { countries, getCountryCode } from "countries-list";

export const INDIAN_BUDGET_UNITS = {
  K: "Thousand",
  L: "Lakh",
  Cr: "Crore",
};

export const WESTERN_BUDGET_UNITS = {
  K: "Thousand",
  M: "Million",
  B: "Billion",
};

const INDIAN_CODES = new Set(["IN", "PK", "BD", "NP", "LK"]);

const COUNTRY_ALIASES = {
  dubai: "AE",
  bharat: "IN",
};

export const COUNTRIES = Object.entries(countries)
  .map(([code, data]) => ({
    code,
    name: data.name,
    currency: data.currency?.[0] || "INR",
    numbering: INDIAN_CODES.has(code) ? "indian" : "western",
    phone: data.phone?.[0] != null ? String(data.phone[0]) : "",
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const COUNTRIES_BY_CODE = Object.fromEntries(
  COUNTRIES.map((country) => [country.code, country]),
);

export const COUNTRY_OPTION_LABELS = COUNTRIES.map(
  (country) => `${country.code} — ${country.name}`,
);

export const DEFAULT_COUNTRY_CODE = "IN";

export const getCountryByCode = (code) => {
  if (!code) return undefined;
  return COUNTRIES_BY_CODE[String(code).trim().toUpperCase()];
};

export const getCountryLabel = (code) => {
  const country = getCountryByCode(code);
  if (!country) return "";
  return `${country.code} — ${country.name}`;
};

export const getCountryCodeFromLabel = (label) => {
  if (!label) return "";
  const code = String(label).split("—")[0].trim().toUpperCase();
  return getCountryByCode(code)?.code || "";
};

export const getCountryName = (code) =>
  getCountryByCode(code)?.name || "";

export const getDialCode = (countryCode) =>
  getCountryByCode(countryCode)?.phone || "91";

export const getDialLabel = (code) => {
  const country = getCountryByCode(code);
  if (!country?.phone) return "";
  return `+${country.phone} — ${country.name}`;
};

export const DIAL_OPTION_LABELS = COUNTRIES.filter((country) => country.phone).map(
  (country) => `+${country.phone} — ${country.name}`,
);

export const getCountryCodeFromDialLabel = (label) => {
  if (!label) return "";
  const name = String(label).split("—")[1]?.trim();
  if (!name) return "";
  return COUNTRIES.find((country) => country.name === name)?.code || "";
};

export const getCountryCodeFromMobileCountryCode = (
  mobileCountryCode,
  preferredCountryCode,
) => {
  const digits = String(mobileCountryCode || "").replace(/\D/g, "");
  if (!digits) return DEFAULT_COUNTRY_CODE;

  const matches = COUNTRIES.filter((country) => country.phone === digits);
  if (!matches.length) return DEFAULT_COUNTRY_CODE;

  const preferred = getCountryByCode(preferredCountryCode)?.code;
  return (
    matches.find((country) => country.code === preferred)?.code ||
    matches.find((country) => country.code === DEFAULT_COUNTRY_CODE)?.code ||
    matches[0].code
  );
};

export const splitInternationalMobile = (mobile, preferredCountryCode) => {
  const raw = String(mobile || "").trim();
  const digits = raw.replace(/\D/g, "");
  const fallback = {
    phone_country_code: DEFAULT_COUNTRY_CODE,
    national: digits,
  };
  if (!raw.startsWith("+") || !digits) return fallback;

  const matches = COUNTRIES.filter(
    (country) => country.phone && digits.startsWith(country.phone),
  ).sort((a, b) => b.phone.length - a.phone.length);

  if (!matches.length) return fallback;

  const bestLength = matches[0].phone.length;
  const best = matches.filter((country) => country.phone.length === bestLength);
  const preferred = getCountryByCode(preferredCountryCode)?.code;
  const chosen =
    best.find((country) => country.code === preferred) ||
    best.find((country) => country.code === DEFAULT_COUNTRY_CODE) ||
    best[0];

  return {
    phone_country_code: chosen.code,
    national: digits.slice(chosen.phone.length),
  };
};

const INDIAN_CURRENCIES = new Set(
  COUNTRIES.filter((country) => country.numbering === "indian").map(
    (country) => country.currency,
  ),
);

export const getNumberingSystem = (countryCode) =>
  getCountryByCode(countryCode)?.numbering || "indian";

export const getNumberingSystemForCurrency = (currencyCode) => {
  const code = String(currencyCode || "").trim().toUpperCase();
  return INDIAN_CURRENCIES.has(code) ? "indian" : "western";
};

export const getBudgetUnits = (countryCode) =>
  getNumberingSystem(countryCode) === "western"
    ? WESTERN_BUDGET_UNITS
    : INDIAN_BUDGET_UNITS;

export const getBudgetUnitsForCurrency = (currencyCode) =>
  getNumberingSystemForCurrency(currencyCode) === "western"
    ? WESTERN_BUDGET_UNITS
    : INDIAN_BUDGET_UNITS;

export const getDefaultBudgetUnit = (countryCode) =>
  getNumberingSystem(countryCode) === "western" ? "M" : "L";

export const getDefaultBudgetUnitForCurrency = (currencyCode) =>
  getNumberingSystemForCurrency(currencyCode) === "western" ? "M" : "L";

export const getCurrencyForCountry = (countryCode) =>
  getCountryByCode(countryCode)?.currency || "INR";

export const DEFAULT_CURRENCY_CODE = "INR";

const currencyDisplayNames = new Intl.DisplayNames(["en"], { type: "currency" });

const getCurrencyName = (code) => {
  try {
    return currencyDisplayNames.of(code) || code;
  } catch {
    return code;
  }
};

export const CURRENCY_CODES = [
  ...new Set(COUNTRIES.map((country) => country.currency)),
].sort();

export const CURRENCY_OPTION_LABELS = CURRENCY_CODES.map(
  (code) => `${code} — ${getCurrencyName(code)}`,
);

export const getCurrencyLabel = (code) => {
  const normalized = String(code || "").trim().toUpperCase();
  if (!CURRENCY_CODES.includes(normalized)) return "";
  return `${normalized} — ${getCurrencyName(normalized)}`;
};

export const getCurrencyCodeFromLabel = (label) => {
  if (!label) return "";
  const code = String(label).split("—")[0].trim().toUpperCase();
  return CURRENCY_CODES.includes(code) ? code : "";
};

export const resolveCurrencyCode = (code) => {
  const normalized = String(code || "").trim().toUpperCase();
  return CURRENCY_CODES.includes(normalized) ? normalized : DEFAULT_CURRENCY_CODE;
};

export const resolveCountryCode = (property = {}) => {
  const direct = getCountryByCode(property.country_code);
  if (direct) return direct.code;

  const raw = String(property.country || "").trim();
  if (!raw) return DEFAULT_COUNTRY_CODE;

  const asCode = getCountryByCode(raw);
  if (asCode) return asCode.code;

  const fromLabel = getCountryCodeFromLabel(raw);
  if (fromLabel) return fromLabel;

  const alias = COUNTRY_ALIASES[raw.toLowerCase()];
  if (alias) return alias;

  const fromPackage = getCountryCode(raw);
  if (fromPackage) return fromPackage;

  const byName = COUNTRIES.find(
    (country) => country.name.toLowerCase() === raw.toLowerCase(),
  );
  return byName?.code || DEFAULT_COUNTRY_CODE;
};
